import {
    APPLICANT_DETAILS_UPDATED,
    APPLICATION_CREATED,
    APPLICATION_DECISION,
    APPLICATION_STARTED,
    CHECK_STARTED,
    EMAIL,
    NOTE,
    REMINDER,
    STAFF_ASSIGNED,
    UPLOAD,
} from 'proxyaddress-common/constants';
import { ActivityLogEntryType, Applicant, LogType, StaffUser } from 'proxyaddress-common/types';
import { and, equals, filter, isEmpty, isNil, whereEq } from 'ramda';
import { IconOptions } from '../components/bits/Icons';
import { foxtrot, hotel } from '../theme/sizing';

export const checkHasNotStartedApplication = (applicant: Applicant): boolean => {
    // If already has an activity log of logType 'APPLICATION_STARTED' with the same application uuid as the current application,
    // means applicant has already started the application once but have navigated backwards through the application process
    const currentApplicationUuid = applicant.application?.applicationUuid;

    const isApplicationStartType = whereEq({ logType: APPLICATION_STARTED });
    const isStartedCurrentApplication = (logEntry: ActivityLogEntryType) => {
        const applicationLogBelongsTo = logEntry.details?.applicationUuid;
        return and(isApplicationStartType(logEntry), equals(applicationLogBelongsTo, currentApplicationUuid));
    };

    return isEmpty(filter(isStartedCurrentApplication, applicant.activityLog));
};

export const getLogTitle = (logEntry: ActivityLogEntryType, staffUsers: Record<string, StaffUser>): string => {
    const {
        logType,
        details: { checkId, newStaffAssignedUuid },
    } = logEntry;

    if (checkId) {
        return `Check ${logType === CHECK_STARTED ? 'started' : 'returned'}`;
    }

    const staffUserCreatedByName = staffUsers[logEntry.createdBy]?.name || logEntry.createdBy;

    switch (logType) {
        case APPLICATION_CREATED:
            return `Application created by ${staffUserCreatedByName}`;
        case APPLICATION_STARTED:
            return `Application started`;
        case APPLICATION_DECISION:
            return 'Application closed';
        case APPLICANT_DETAILS_UPDATED:
            return `Details updated by ${staffUserCreatedByName}`;
        case STAFF_ASSIGNED:
            return `Assigned to ${newStaffAssignedUuid && staffUsers[newStaffAssignedUuid].name}`;
        case NOTE:
            return `Note added by ${staffUserCreatedByName}`;
        case EMAIL:
            return `Email sent by ${staffUserCreatedByName}`;
        case REMINDER:
            return `Reminder sent by ${staffUserCreatedByName}`;
        case UPLOAD:
            return `File uploaded by ${staffUserCreatedByName}`;
        default:
            return '';
    }
};

export const getLogIcon = (logEntry: ActivityLogEntryType): IconOptions => {
    const {
        logType,
        details: { checkType },
    } = logEntry;

    if (checkType) {
        return 'comment';
    }

    switch (logType) {
        case CHECK_STARTED:
            return 'idCheck';
        case APPLICATION_CREATED:
            return 'creation';
        case APPLICATION_STARTED:
            return 'tick';
        case STAFF_ASSIGNED:
            return 'addCircle';
        case UPLOAD:
            return 'document';
        default:
            return 'comment';
    }
};

export const noteOptions = [
    { value: NOTE, label: 'Note' },
    { value: EMAIL, label: 'Email sent' },
    { value: REMINDER, label: 'Reminder sent' },
    { value: UPLOAD, label: 'Document upload' },
];

export const getLogIconSize = (logType: LogType): string => {
    switch (logType) {
        case STAFF_ASSIGNED:
        case APPLICATION_STARTED:
        case UPLOAD:
            return foxtrot;
        default:
            return hotel;
    }
};

interface CreateActivityLogEntryErrors {
    noteError: string;
    noteTypeError: string;
    fileError: string;
}

export const initialCreateActivityLogEntryErrors = {
    noteError: '',
    noteTypeError: '',
    fileError: '',
};

export const checkActivityLogNoteToCreate = (
    note: string,
    noteType: string | undefined,
    hasFiles: boolean,
): CreateActivityLogEntryErrors => {
    return {
        noteError: isEmpty(note) ? 'Please enter a note' : '',
        noteTypeError: isNil(noteType) ? 'Please select a note type' : '',
        fileError: noteType === UPLOAD && !hasFiles ? 'Please upload a file' : '',
    };
};
